import { Component, OnInit } from '@angular/core';
import { StimulationForm } from '../../../../models/StimulationForm';
import { Treatment } from '../../../../models/Treatment';
import { TreatmentService } from '../../../../services/treatment.service';
import { ActivatedRoute } from '@angular/router';
import { forkJoin } from 'rxjs';
import { TranslatePipe } from '@ngx-translate/core';
import { ButtonDirective } from 'primeng/button';
import { StimulationFormA4PrintComponent } from '../stimulation-form-a4-print/stimulation-form-a4-print.component';
import { StimulationPrintModel } from '../stimulation-form-a4-print/stimulation-print.model';
import { StimulationPrintService } from '../stimulation-form-a4-print/stimulation-print.service';

/**
 * Print preview of the stimulation form. Shows the A4 portrait sheet
 * ("Stimulaatiokaavakkeen jatkokehitys") exactly as it will be printed: one
 * treatment day per row, one page per treatment, on a real A4 page and not on
 * US Letter.
 *
 * The sheet itself is built and printed by StimulationPrintService, the same
 * one the stimulation form's Tulosta button uses, so the preview and the
 * printout can never drift apart.
 */
@Component({
  selector: 'app-stimulation-form-print',
  templateUrl: './stimulation-form-print.component.html',
  styleUrls: ['./stimulation-form-print.component.scss'],

  imports: [TranslatePipe, ButtonDirective, StimulationFormA4PrintComponent]
})
export class StimulationFormPrintComponent implements OnInit {
  private treatmentId: string | undefined;
  public stimulationForm?: StimulationForm;
  public treatment: Treatment | null = null;
  public model: StimulationPrintModel | null = null;

  /** "Tulosta ultraäänet" — on by default; off frees room for more days. */
  public showUltrasounds = true;

  private autoPrint = false;

  public constructor(
    private activatedRoute: ActivatedRoute,
    private treatmentService: TreatmentService,
    private printService: StimulationPrintService
  ) {}

  ngOnInit(): void {
    this.treatmentId = this.activatedRoute.snapshot.params.treatmentId;
    // The switch may be preset from the calling view (?ultrasounds=0).
    const params = this.activatedRoute.snapshot.queryParamMap;
    if (params.get('ultrasounds') === '0') {
      this.showUltrasounds = false;
    }
    // Opened from a print button: show the print dialog once the sheet is
    // ready, the way the previous PDF export did.
    this.autoPrint = params.get('autoprint') === '1';
    if (!this.treatmentId) {
      return;
    }
    forkJoin([
      this.treatmentService.getTreatment(this.treatmentId),
      this.treatmentService.getStimulationFormByTreatmentId(this.treatmentId)
    ]).subscribe(([treatment, form]) => {
      this.treatment = treatment;
      this.stimulationForm = form;
      void this.buildModel();
    });
  }

  /** Re-renders the sheet when the ultrasound switch is toggled. */
  onToggleUltrasounds(): void {
    this.showUltrasounds = !this.showUltrasounds;
    void this.buildModel();
  }

  /** Prints the sheet that is on screen. */
  print(): void {
    if (this.model) {
      void this.printService.printModel(this.model);
    }
  }

  private async buildModel(): Promise<void> {
    if (!this.treatment || !this.stimulationForm) {
      return;
    }
    this.model = await this.printService.buildModel(
      this.treatment,
      this.stimulationForm,
      this.showUltrasounds
    );
    if (this.autoPrint) {
      this.autoPrint = false;
      void this.printService.printModel(this.model);
    }
  }
}
